// src/pages/About.js
// MODERNIZED: Nulek brand colors, charcoal palette (#222222/#2E2E2E), Barlow font
// PRESERVED: All state, IntersectionObserver, serviceCardsRef, image imports, all copy

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import styles from './About.module.css';
import homeImage from '../images/about/nulek-home.png';

const About = () => {
  const [isMobile, setIsMobile] = useState(false);
  const serviceCardsRef = useRef([]);

  // resize handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // IntersectionObserver for card fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );
    serviceCardsRef.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const coreServices = [
    { title: 'Planning & Scheduling', desc: 'Nulek Consulting provides a comprehensive planning and scheduling services that support projects from initiation through to project closeout. Our services ensure that clients receive clear, accurate, and defensible schedules that meet contractual, regulatory, and operational requirementsEnd-to-end support from EOI & tender schedules, baseline development, to construction-phase updates and look-ahead planning — ensuring schedules are realistic, defensible, and contract-compliant.' },
    { title: 'Schedule Risk Analysis (SRA)', desc: 'We provide a comprehensive Schedule Risk Analysis (SRA) services to help organisations understand the uncertainty, risk exposure, and probability of achieving key project milestones. Our approach ensures that project schedules are not only technically sound but also tested for realism and resilience under a variety of risk conditions' },
    { title: 'Time–Chainage Diagrams', desc: 'Nulek Consulting specialises in producing high-quality Time–Chainage diagrams using industry-leading software such as TILOS and Turbo-Chart. These visual scheduling tools are essential for linear and infrastructure projects, providing clear, intuitive, and geographically aligned representations of project activities. Our diagrams help project teams, contractors, and stakeholders understand construction sequencing, access constraints, and production rates—all in a single, powerful visual. ' },
    { title: 'Power BI Dashboards', desc: 'At Nulek Consulting, we deliver advanced Power BI reporting solutions that transform complex project data into clear, interactive, and highly visual dashboards. Our reporting services empower project teams and executives with real-time insights for effective decision-making across planning, scheduling, risk, cost, and overall project performance' },
    { title: 'Training & Capability Uplift', desc: 'At Nulek Consulting, we deliver practical, industry-focused training designed to build strong foundational skills in project planning and scheduling. Our Planning and Scheduling Fundamentals program equips participants with the knowledge and tools needed to confidently develop, manage, and analyse project schedules across a wide range of industries.These trainings are ideal for project engineers, site supervisors, coordinators, emerging planners, and organisations looking to uplift their project controls capability.' },
  ];

  const values = [
    { title: 'Lifecycle Coverage', text: 'Support from <strong>EOI scheduling</strong> and <strong>tender programs</strong>, through <strong>baseline formalisation</strong>, <strong>construction-phase controls</strong>, to <strong>forensic delay analysis</strong> and closeout reporting.' },
    { title: 'Specialised Tools & Methods', text: 'Expert use of <strong>Primavera P6</strong>, <strong>MS Project</strong>, <strong>TILOS/Turbo-Chart</strong> for linear works, and <strong>Power BI</strong> for real-time EVM dashboards — ensuring fit-for-purpose solutions.' },
    { title: 'Risk-Informed Planning', text: '<strong>Quantitative Schedule Risk Analysis (QSRA)</strong> using Monte Carlo simulation (P20/P50/P80), risk mapping, sensitivity analysis, and contingency modelling — moving beyond deterministic optimism.' },
  ];

  return (
    <div className={styles.container}>
      <Helmet>
        <title>About Us | Nulek Consulting - Australian Project Controls Experts</title>
        <meta name="description" content="Nulek Consulting is a specialist Australian consultancy providing end-to-end project planning, scheduling, and risk analysis for infrastructure and construction." />
      </Helmet>

      {/* ── HERO ── */}
      <section className={`${styles.hero} ${isMobile ? styles.heroMobile : ''}`}>
        <div className={styles.heroAccentBar} />
        <div className={styles.heroContent}>
          <p className={styles.eyebrow}>Who We Are</p>
          <h1 className={`${styles.heroTitle} ${isMobile ? styles.heroTitleMobile : ''}`}>
            About Nulek Consulting
          </h1>
          <p className={`${styles.heroText} ${isMobile ? styles.heroTextMobile : ''}`}>
            An Australian planning and scheduling consultancy delivering end-to-end project controls
            across building, water, rail, energy, and utilities infrastructure — from tender to closeout.
          </p>
        </div>
      </section>

      {/* ── WHO WE ARE ── */}
      <section className={`${styles.section} ${styles.sectionLight}`}>
        <div className={styles.contentContainer}>
          <div className={`${styles.missionRow} ${isMobile ? styles.missionRowMobile : ''}`}>
            <div className={styles.missionText}>
              <p className={styles.eyebrow}>Our Story</p>
              <h2 className={`${styles.sectionTitle} ${isMobile ? styles.sectionTitleMobile : ''}`}>Who We Are</h2>
              <p className={styles.paragraph}>
                <strong>Nulek Consulting</strong> is an Australian planning and scheduling consultancy
                delivering services across the full project life cycle — from tendering and project delivery
                through to project closeout.
              </p>
              <p className={styles.paragraph}>
                With a strong foundation in project controls, we provide end-to-end planning support:
                baseline development, detailed scheduling, earned value management, progress analysis,
                and forensic delay reviews.
              </p>
              <p className={styles.paragraph}>
                Our focus is to help clients make informed decisions that lead to <strong>on-time</strong>,{' '}
                <strong>on-budget</strong>, and <strong>high-quality</strong> project outcomes — across
                building construction, water infrastructure, rail, energy, and utilities.
              </p>
            </div>

            <div className={styles.missionImageContainer}>
              <div className={styles.missionImageWrapper}>
                <img
                  src={homeImage}
                  alt="Nulek Consulting team and project work"
                  className={`${styles.missionImage} ${isMobile ? styles.missionImageMobile : ''}`}
                />
                <div className={styles.missionImageAccent} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR APPROACH ── */}
      <section className={`${styles.section} ${styles.sectionDark}`}>
        <div className={`${styles.contentContainer} ${styles.contentContainerCentered}`}>
          <p className={`${styles.eyebrow} ${styles.eyebrowCentered}`}>How We Work</p>
          <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLight} ${isMobile ? styles.sectionTitleMobile : ''}`}>Our Approach</h2>
          <p className={`${styles.paragraph} ${styles.paragraphLight} ${isMobile ? styles.paragraphMobile : ''}`}>
            We combine deep technical expertise with a collaborative, stakeholder-first mindset.
            We work closely with trades, design teams, project managers, and executives to translate
            strategic objectives into <strong>actionable, measurable, and transparent</strong> plans.
          </p>
          <p className={`${styles.paragraph} ${styles.paragraphLight} ${isMobile ? styles.paragraphMobile : ''}`}>
            Whether supporting a single project or an entire programme, we deliver the structure,
            insight, and governance framework needed to keep work moving — efficiently, predictably,
            and with confidence.
          </p>
        </div>
      </section>

      {/* ── CORE SERVICES ── */}
      <section className={`${styles.section} ${styles.sectionLight}`}>
        <div className={styles.contentContainer}>
          <p className={styles.eyebrow}>What We Offer</p>
          <h2 className={`${styles.sectionTitle} ${isMobile ? styles.sectionTitleMobile : ''}`}>Our Services</h2>
          <p className={`${styles.sectionSubtitle} ${isMobile ? styles.sectionSubtitleMobile : ''}`}>
            Comprehensive planning, scheduling, risk, and reporting solutions — tailored to every phase of your project lifecycle.
          </p>

          <div className={styles.servicesGrid}>
            {coreServices.map((service, idx) => (
              <div
                key={idx}
                ref={(el) => (serviceCardsRef.current[idx] = el)}
                className={styles.serviceCard}
              >
                <div className={styles.serviceAccent} />
                <h3 className={styles.serviceTitle}>{service.title}</h3>
                <p className={styles.serviceText}>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES SUMMARY ── */}
      <section className={`${styles.section} ${styles.sectionDarker}`}>
        <div className={styles.contentContainer}>
          <p className={`${styles.eyebrow} ${styles.eyebrowCentered}`}>Our Capabilities</p>
          <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLight} ${styles.sectionTitleCentered} ${isMobile ? styles.sectionTitleMobile : ''}`}>
            End-to-End Project Controls, Backed by Technical Rigour
          </h2>
          <div className={styles.valuesGrid}>
            {values.map((v, i) => (
              <div key={i} className={styles.valueCard}>
                <div className={styles.valueNumber}>{i + 1}</div>
                <h3 className={styles.valueTitle}>{v.title}</h3>
                <p className={styles.valueText} dangerouslySetInnerHTML={{ __html: v.text }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;